/**
 * Headless bot-vs-bot matches: the shipped bot pipeline (BotTurnRunner →
 * planner → fire/move/jump search) driven against a host that resolves every
 * action instantly with the game's own core functions — no Three.js, no DOM,
 * no clock. Same seed → same match, so a report is reproducible.
 *
 * Use it to measure match shape (turns to first blood, hit rates by skill,
 * miss distances, how often nobody moves, how long games run) before and after
 * a bot or rules change.
 *
 * Scope: every mode preset and any stacked modifier (core/modeRules.ts). The
 * host resolves the mode actions (stakes, ruins, carryables, collectibles,
 * drills, claims) with the same core functions Game.perform* uses.
 *
 * Turn flows (Phase 40): 'perUnit' resolves every action as it happens. In the
 * round flows ('simultaneous', 'allPlan') the host does what Game does for a
 * bot — a shot becomes a prepaid order with a launch snapshot, a walk or jump is
 * charged now and planned — and the whole queue flies through `simulateRound`
 * when control leaves the planning teams (simultaneous) or the planning order
 * wraps (allPlan). 'batched' only changes human play; an all-bot match plays
 * exactly as 'perUnit'.
 */

import { squadOf } from '../core/squads';
import type {
  GameState, PlayerState, FireParams, JetpackParams, ShopParams, StructureType, ItemKey, Vec3,
  CollectActionParams, BotFogContext, BotConfig,
  TurnFlow, AlertLevel,
} from '../types';
import {
  isGameOver, checkWinCondition,
} from '../core/gameState';
import { SeededRandom } from '../core/random';
import { checkFire } from '../core/actions';
import { type QueuedOrder, type PlannedMove } from '../core/turnFlow';
import { type Loudness } from '../core/perception';
import { matchDisplayName } from '../core/modeRules';
import { summarizeShots, isHighlightShot } from '../core/shotRecord';
import {
  PHYS_DT, WEAPONS,
  BOT_SKILL_LEVELS, MAX_AIRBORNE_TIME,
} from '../constants';
import { computeBotFire, computeBotJump } from './bot';
import { buildTransitGraph } from './transitGraph';
import { isWasmReady, setPlanets, buildWasmScorer, createKernelWorld } from './wasmPhysics';
import { computeTurnPlan, transitFuelBonus } from './botPlanner';
import { BotTurnRunner, type BotHost } from './botTurnRunner';
import type { TurnAction, TurnInput } from '../types';
import { Match, type SimPresenter, type AirborneStep, type PlanResult, type TurnPass, type RoundClose } from '../match/match';
import { createMatchState, createMatch, matchFromState, type MatchSetup } from '../match/setup';
import type { BotWorkerClient } from './botWorkerClient';
import { computeStateChecksum } from '../network/protocol';
import { dhypot } from '../core/dmath';

// =============================================================================
// Options and report
// =============================================================================

/** The setup of a match (src/match/setup.ts) — the harness's name for it. */
export type HeadlessMatchOptions = MatchSetup;

export interface HeadlessEvent {
  round: number;
  playerId: number;
  kind: string;
  detail?: string;
}

export interface PlayerReport {
  id: number;
  name: string;
  team: number;
  skill: number;
  alive: boolean;
  health: number;
  damageDealt: number;
  kills: number;
  shots: number;
  hits: number;
  assists: number;
  turns: number;
  movedTurns: number;
  jumpedTurns: number;
}

/**
 * How players died: the blast itself (incl. hitscan and melee), the landing
 * after being airborne, or leaving the field. `jumpLanding` / `jumpSpace` are
 * the subsets of `landing` / `space` where the unit was airborne by its own
 * jump rather than a launch.
 */
export interface KillsByCause {
  blast: number;
  landing: number;
  space: number;
  jumpLanding: number;
  jumpSpace: number;
}

/** 56.12: landing damage of the infiltrating teams' own jumps (cause 'jump'; non-guard teams only). */
export interface InfiltratorLandings { jumps: number; meanDamage: number; maxDamage: number; deaths: number }

/** One landing as the host records it (both the unit-by-unit and the round paths). */
export interface LandingRecord { playerId: number; teamIndex: number; cause: string; damage: number; died: boolean }

export function summarizeInfiltratorLandings(landings: readonly LandingRecord[], guardTeams: number[] | 'all'): InfiltratorLandings {
  const own = landings.filter(l => l.cause === 'jump' && guardTeams !== 'all' && !guardTeams.includes(l.teamIndex));
  const jumps = own.length;
  const mean = jumps ? own.reduce((a, l) => a + l.damage, 0) / jumps : 0;
  return { jumps, meanDamage: Math.round(mean * 10) / 10, maxDamage: Math.round(own.reduce((a, l) => Math.max(a, l.damage), 0)), deaths: own.filter(l => l.died).length };
}

export interface MatchReport {
  /** The hidden archetypes each team played (MatchSetup.archetypes on; absent off). */
  archetypes?: string[];
  seed: string;
  options: Required<Pick<HeadlessMatchOptions, 'teams' | 'unitsPerTeam' | 'planets' | 'maxRounds'>>;
  /** Units per team as played (uneven teams differ from options.unitsPerTeam). */
  unitsByTeam: number[];
  /** Credits per unit each team fought with. */
  cbpByTeam: number[];
  /** Phase 57: the alert layer's story, when the match had one. */
  alert?: { startingAlert: AlertLevel; guardTeams: number[] | 'all'; firstSuspiciousRound: number | null; firstAlertRound: number | null; wentLoud: boolean; finalLevels: Record<number, AlertLevel>;
    /** 56.12: the infiltrating (non-guard) teams' own jumps — how many, what they cost, how many killed. The round-trip gate (tenth fill). */
    infiltratorLandings: InfiltratorLandings };
  difficulty: string[];
  /** Display name of the match rules ("Dominion + Blackout"). */
  mode: string;
  turnFlow: TurnFlow;
  /** Round flows: how many planned rounds were resolved through the round simulator. */
  roundsResolved: number;
  /** Per-team kit: the preset name, or 'custom'. */
  kits: string[];
  rounds: number;
  /** Seconds of fighting — action-clock matches only (R7: an action battle's length is seconds, not rounds). */
  seconds?: number;
  botTurns: number;
  winnerTeam: number | null;
  reason: string | null;
  killsByCause: KillsByCause;
  /** Per-team objective standing at the end (captures, territory, fragments, ...), or null for deathmatch. */
  objective: Record<number, string> | null;
  /** Round of the first damage dealt to a player, or null. */
  firstDamageRound: number | null;
  firstKillRound: number | null;
  shots: number;
  hits: number;
  hitRate: number;
  lost: number;
  lostRate: number;
  assists: number;
  highlightShots: number;
  /** Miss distance vs the bot's intended target: mean and median over shots with a target. */
  meanMiss: number | null;
  medianMiss: number | null;
  /** Per-skill breakdown of shots → hits, miss median. */
  bySkill: Record<string, { shots: number; hits: number; medianMiss: number | null; assists: number }>;
  actions: Record<string, number>;
  /** Fraction of bot turns in which the bot walked or jumped. */
  movementTurnRate: number;
  /** Fraction of bot turns that were fire only (no movement). */
  fireOnlyTurnRate: number;
  /** Fraction of bot turns with no action at all (yield). */
  idleTurnRate: number;
  /** X11 (with callIns): the call-ins asked, and how many were answered with a mission or refused. */
  callIns?: { asked: number; fired: number; refused: number; missions?: number; marks?: number };
  /** Seconds of projectile flight the players would have watched. */
  flightSeconds: number;
  /** Total airborne-player seconds (jumps and knockbacks). */
  airborneSeconds: number;
  /** Bot thinking time (WASM kernel when loaded, else JS): mean and max per bot turn. */
  meanComputeMs: number;
  maxComputeMs: number;
  totalMs: number;
  unsupported: number;
  players: PlayerReport[];
  events: HeadlessEvent[];
  /** The events plus the bots' decision trace (plan chosen, jump skipped and why) in order — `--events` prints this; the golden locks `events`. */
  trace: HeadlessEvent[];
  /** With `traceTurns`: the game's `?trace` lines — `t<turn> p<current> <checksum hex>` at every turn advance. */
  turnTrace?: string[];
  /** With `traceChecksums`: the P2P state checksum after every bot turn, then the final state. */
  checksums?: number[];
}

// =============================================================================
// Match construction
// =============================================================================

/** Build the deployed, fogged, ready-to-aim state for an all-bot match (createMatchState, src/match/setup.ts). */
export function createHeadlessMatch(opts: HeadlessMatchOptions): { state: GameState; difficulty: string[] } {
  return createMatchState(opts);
}

// =============================================================================
// Headless host
// =============================================================================

// A few seconds past the core cap: a unit that is still airborne here escaped the physics, not the clock.

// A shot and all its bomblets, with the delays between them: far more steps than any real flight takes.


export class HeadlessHost implements BotHost {
  /** The match runtime the game uses (src/match/match.ts); this host moves onto it step by step (Phase 51). */
  readonly match: Match;
  get state(): GameState { return this.match.state; }
  set state(v: GameState) { this.match.state = v; }
  /** The kernel, when loaded, for line of sight and flights — as the game uses it. */
  get useWasm(): boolean { return this.match.gameWasm; }
  readonly worker: BotWorkerClient;
  readonly events: HeadlessEvent[] = [];
  readonly trace: HeadlessEvent[] = [];
  /** 56.12: every landing, for the infiltration landing-damage summary. */
  readonly landings: LandingRecord[] = [];
  private recordLanding(playerId: number, cause: string, damage: number, died: boolean): void {
    const p = this.state.players.find(x => x.id === playerId);
    this.landings.push({ playerId, teamIndex: p?.teamIndex ?? -1, cause, damage, died });
  }
  readonly actions: Record<string, number> = {};
  /** Per bot turn: what happened, for movement/idle rates. */
  readonly turnActions: Array<{ playerId: number; round: number; acts: string[] }> = [];
  flightSeconds = 0;
  airborneSeconds = 0;
  unsupported = 0;
  endTurns = 0;
  /** The alert levels before an advance (the presenter's turn members compare them after it). */
  private alertsBefore: NonNullable<GameState['modeState']['alert']>['byPlanet'] | undefined;
  gameOver = false;
  /** With `traceTurns`: the game's ?trace lines. */
  turnTrace: string[] | null = null;
  roundsResolved = 0;

  // Round flows: what the planning teams have queued (on the Match, as in the game)
  private get batchQueue(): QueuedOrder[] { return this.match.batchQueue; }
  private set batchQueue(v: QueuedOrder[]) { this.match.batchQueue = v; }

  private get currentShot(): Match['currentShot'] { return this.match.currentShot; }
  private set currentShot(v: Match['currentShot']) { this.match.currentShot = v; }
  private advancing = false;
  /** 64.6 step 4: the boundary's answer the policy presented after a round (windowOpened), taken by followPass. */
  private windowPass: TurnPass | null = null;

  /** Host a Match (createMatch), or a ready state wrapped in one. */
  constructor(from: GameState | Match) {
    this.match = from instanceof Match ? from : matchFromState(from);
    this.worker = this.makeSyncWorker();
  }

  // ---- bookkeeping -----------------------------------------------------------

  private get round(): number { return this.state.turnNumber; }
  private cur(): PlayerState { return this.state.players[this.state.currentPlayerIndex]; }
  private player(id: number): PlayerState | undefined { return this.state.players.find(p => p.id === id); }
  private get roundFlow(): boolean { const f = this.state.config.turnFlow; return f === 'simultaneous' || f === 'allPlan'; }
  /** BotHost: in round flows the runner shoots first, then spends what is left on planned movement. */
  get plannedRound(): boolean { return this.roundFlow; }

  private event(kind: string, playerId: number, detail?: string): void {
    this.events.push({ round: this.round, playerId, kind, detail });
    this.trace.push({ round: this.round, playerId, kind, detail });
  }
  private act(kind: string): void {
    this.actions[kind] = (this.actions[kind] ?? 0) + 1;
    const t = this.turnActions[this.turnActions.length - 1];
    if (t) t.acts.push(kind);
  }
  beginTurn(playerId: number): void {
    this.turnActions.push({ playerId, round: this.round, acts: [] });
  }

  setState(next: GameState): void { this.state = next; }
  buildFogContext(player: PlayerState): BotFogContext | undefined {
    return this.match.botContext(player);   // the game's bot host asks the same
  }
  showThinking(): void { /* headless */ }
  hideThinking(): void { /* headless */ }
  log(playerId: number, message: string): void { this.event('log', playerId, message); }
  note(playerId: number, kind: string, detail: string): void { this.trace.push({ round: this.round, playerId, kind, detail }); }
  taunt(): void { /* headless */ }

  /** The planner and searches run inline; the runner awaits them like the worker. */
  private makeSyncWorker(): BotWorkerClient {
    const host = this;
    const graphFor = (player: PlayerState, state: GameState) =>
      buildTransitGraph(state.planets, player.movementRemaining, transitFuelBonus(player, state.config.modeConfig), player.planetIndex);
    return {
      async computeBotTurnAsync(player: PlayerState, state: GameState, config: BotConfig, rngState: number, fog?: BotFogContext) {
        host.match.enterKernel();
        const rng = SeededRandom.fromState(rngState);
        const scorer = buildWasmScorer(state.planets); // pushes planets for the transit/jump batches too
        return computeTurnPlan(player, state, config, rng, fog, graphFor(player, state), scorer);
      },
      async computeBotFireAsync(player: PlayerState, state: GameState, config: BotConfig, rngState: number, fog?: BotFogContext) {
        host.match.enterKernel();
        const rng = SeededRandom.fromState(rngState);
        const result = computeBotFire(player, state, config, rng, fog, buildWasmScorer(state.planets));
        return { result, rngState: rng.getState() };
      },
      async computeBotJumpAsync(player: PlayerState, state: GameState, config: BotConfig, rngState: number, fogContext?: BotFogContext) {
        host.match.enterKernel();
        const rng = SeededRandom.fromState(rngState);
        if (isWasmReady()) setPlanets(state.planets);
        const result = computeBotJump(player, state, config, rng, graphFor(player, state), undefined, fogContext);
        return { result, rngState: rng.getState() };
      },
    } as unknown as BotWorkerClient;
  }

  // ---- fog ---------------------------------------------------------------------

  /** Match.refreshFog, as the game: the unit's line of sight (blackout-aware) and ghost markers. */
  private refreshFog(playerIndex: number): void {
    this.match.refreshFog(playerIndex);
  }

  /**
   * R11 step 2: every action enters the Match as an input through Match.apply, stamped for this bot
   * (a brain's actor id is its unit id). The result is the action method's own, so the wrappers below
   * read it as before; the Match logs the input, and refusals with their code.
   */
  private turn(action: TurnAction, params: TurnInput['params']): PlanResult<object> {
    const player = this.cur();
    return this.match.apply(this.match.stamp(player.id, { kind: 'turn', input: { playerId: player.id, action, params } }), this.sim);
  }

  // ---- fire --------------------------------------------------------------------

  /** 62.43: the commander's missions and the marks the bots made (the report's call-in block). */
  readonly asks = { missions: 0, marks: 0 };
  fireMission(team: number, gunId: number, planetIndex: number, surfaceDir: Vec3): boolean {
    const ok = this.match.seatFireMission(team, gunId, planetIndex, surfaceDir);
    if (ok) this.asks.missions++;
    if (ok) this.log(gunId, `the commander orders fire from ${this.match.state.players.find(p => p.id === gunId)?.name ?? gunId}`);
    return ok;
  }
  mark(team: number, mark: { planetIndex: number; surfaceDir: Vec3; unitId?: number }): boolean {
    const n = this.match.seatMark(team, mark);
    if (n) this.asks.marks++;
    if (n) this.log(this.cur().id, `${this.cur().name} marks ${this.match.state.players.find(p => p.id === mark.unitId)?.name ?? 'a point'}`);
    return n > 0;
  }
  callIn(planetIndex: number, surfaceDir: Vec3): boolean {
    const player = this.cur();
    const sq = squadOf(this.match.state, player.id);
    if (!sq) return false;
    const r = this.match.apply(this.match.stamp(player.id, { kind: 'order', team: player.teamIndex, to: { squad: sq.id }, tier: 'task', order: { kind: 'callIn', planetIndex, surfaceDir } }), this.sim);
    this.log(player.id, r.ok ? `${player.name} calls in fire` : `${player.name} calls in fire — refused: ${r.reason}`);
    return r.ok;
  }

  fire(params: FireParams): boolean {
    const player = this.cur();
    const weapon = WEAPONS[params.weaponKey];
    const check = checkFire(player, params.weaponKey);
    if (!check.ok) { this.event('fireRefused', player.id, check.reason); return false; }

    // Round flows (Game.queueOrder): hold fire as a prepaid order; the shot leaves from where the
    // bot stands now and flies in the round. Hitscan has no flight to plan and resolves at once.
    if (this.roundFlow && !weapon.hitscan) {
      this.turn('queue', params);   // a brain's queue: the bot host's prepaid order, its target remembered (Match.queueBotShot)
      this.act(params.weaponKey === 'probe' ? 'probe' : 'fire');
      this.event('queued', player.id, params.weaponKey);
      return true;
    }

    this.act(params.weaponKey === 'probe' ? 'probe' : 'fire');
    // Match.fire, as the game: cost, facing, the shot tracked; a beam resolves now, a projectile and every
    // bomblet fly tick by tick (airborne units step during the flight and between bomblets)
    const fired = this.turn('fire', params) as ReturnType<Match['fire']>;
    if (!fired.ok) { this.event('fireRefused', player.id, fired.reason); return false; }
    if (fired.hitscan) {
      const h = fired.hitscan;
      if (h.hitPlayerId !== null) this.noteDamage([{ playerId: h.hitPlayerId, damage: h.damage }], h.killed ? 1 : 0);
      this.alertEvents(h.alerts);
      return true;
    }
    // 64.1: the Match steps the flight to its end and everyone it left in the air (what the game does over wall time);
    // 64.6 2b-ii: on the tick it settles, the turn policy puts the bot back to aiming (the line below only covers a unit
    // still lost in orbit when the airborne cap ran out, where nothing settles)
    this.settleAirborne();
    this.currentShot = null;
    this.state = { ...this.state, projectile: null, phase: 'aiming' };
    return true;
  }

  private noteDamage(damages: Array<{ playerId: number; damage: number }>, kills: number, shooterId = this.cur().id): void {
    const total = damages.reduce((s, d) => s + d.damage, 0);
    if (total > 0) this.event('damage', shooterId, String(Math.round(total)));
    if (kills > 0) this.event('kill', shooterId, String(kills));
  }


  /** Match.revealProbePath, as the game: terrain, ghost markers and structure ghosts along the whole path. */
  private probeReveal(ownerId: number, points: Vec3[]): void {
    const spotted = this.match.revealProbePath(ownerId, points);
    if (spotted.length > 0) this.event('probeSpotted', ownerId, String(spotted.length));
  }

  // ---- movement ------------------------------------------------------------------

  jump(aim: JetpackParams): void {
    const player = this.cur();
    // Match.jump, as the game: planned in the plan-together flows (charged now, flown in the round), launched otherwise
    const j = this.turn('jetpack', aim) as ReturnType<Match['jump']>;
    if (!j.ok) { this.event('jumpRefused', player.id, j.reason === 'Jump already planned this turn' ? 'jump already planned' : j.reason); return; }
    this.act('jump');
    this.event(j.planned ? 'jumpPlanned' : 'jump', player.id, `planet ${player.planetIndex}`);
  }

  move(targetDir: Vec3): boolean {
    const player = this.cur();
    // Match.move, as the game: planned in the plan-together flows; otherwise movement spent, facing,
    // then a friendly core returns, heal items are picked up, a carrier at home captures
    const m = this.turn('move', { surfaceDir: { ...targetDir } }) as ReturnType<Match['move']>;
    if (!m.ok) {
      const unaffordable = !this.roundFlow && m.reason.startsWith('Too far!');
      this.event('moveRefused', player.id, unaffordable ? 'unaffordable' : m.reason);
      return false;
    }
    this.act('move');
    if (m.planned) { this.event('movePlanned', player.id, m.distance.toFixed(1)); return true; }
    if (m.captured !== null) {
      this.event('capture', player.id, String(m.captured));
      if (isGameOver(this.state)) this.gameOver = true;
    }
    this.event('move', player.id, m.distance.toFixed(1));
    this.refreshFog(this.state.currentPlayerIndex);
    return true;
  }

  /** CTF: per-team flag status follows the carryables (Match). */
  private refreshFlagStatus(): void {
    this.match.refreshFlagStatus();
  }

  /** CTF: a carrier standing at home captures (Match.captureIfHome, as Game.checkAndExecuteCapture). */
  private checkCapture(playerId: number): void {
    const objectId = this.match.captureIfHome(playerId);
    if (objectId === null) return;
    this.event('capture', playerId, String(objectId));
    if (isGameOver(this.state)) this.gameOver = true;
  }

  isWalking(): boolean { return false; }

  /** Fly every airborne player to the ground (or their death). */
  /** One airborne tick (during a flight, or settling after one): its events (Match.stepAirborne refreshed the landed units' fog). */
  private onAirborneStep(step: AirborneStep): void {
    this.airborneSeconds += PHYS_DT;
    for (const e of step.events) {
      if (e.kind === 'landed') {
        this.recordLanding(e.playerId, e.cause, e.damage, e.died);
        this.event(e.died ? 'crashDied' : 'landed', e.playerId, `${e.cause} planet ${e.planetIndex} dmg ${Math.round(e.damage)}`);
      } else if (e.kind === 'diedInStar' || e.kind === 'diedInWormhole' || e.kind === 'lostInSpace') {
        this.event('lostInSpace', e.playerId, `${e.cause} ${e.kind === 'diedInStar' ? 'star' : e.kind === 'diedInWormhole' ? 'wormhole' : 'space'}`);
      } else if (e.kind === 'cargoRespawned') {
        this.event('cargoRespawned', e.playerId);
      }
    }
  }

  /** The harness's side of the simulation clock: count, record events; nothing to animate. */
  private readonly sim: SimPresenter = {
    // 64.6 step 2: this host's view of a boundary's hops, at the points it always acted — the `?trace` line before an
    // advance (the game never advances a finished match), the turn's log and the alert changes after it, a hazard's event
    turnAdvancing: () => {
      if (!isGameOver(this.state)) this.turnTrace?.push(`t${this.state.turnNumber} p${this.state.currentPlayerIndex} ${(computeStateChecksum(this.state) >>> 0).toString(16).padStart(8, '0')}`);
      this.alertsBefore = this.state.modeState.alert?.byPlanet;
    },
    turnAdvanced: a => {
      for (const l of a.r.logs) this.event('turnLog', -1, l);
      const before = this.alertsBefore, after = this.state.modeState.alert?.byPlanet;
      if (before && after) for (const k of Object.keys(after)) { const i = Number(k); if (before[i]?.level !== after[i].level) this.event('alert', -1, `planet ${i} ${before[i]?.level} → ${after[i].level} (turn)`); }
    },
    roundClosed: close => this.roundClosed(close),
    windowOpened: pass => { this.windowPass = pass; },   // 64.6 step 4: the policy opened the window after a round
    proximityApplied: prox => {
      if (prox) this.event('hazard', prox.playerId, `${prox.damage} dmg${prox.launched ? ' launched' : ''}${prox.died ? ' died' : ''}`);
    },
    holdFlightEnd: () => false,
    flightEndReached: () => {},
    launched: (_projectile, sim) => { this.flightSeconds += (sim.points.length - 1) * PHYS_DT; },
    probeScan: () => {},
    geyserDeflect: () => {},
    impacted: e => {
      this.noteDamage(e.r.damages, e.r.kills.length);
      for (const id of e.r.sheltered) this.event('sheltered', id, 'rim');
      this.alertEvents(e.alerts);
      if (e.probeSpotted.length > 0) this.event('probeSpotted', e.projectile.ownerId, String(e.probeSpotted.length));
    },
    lost: e => { if (e.probeSpotted.length > 0) this.event('probeSpotted', e.projectile.ownerId, String(e.probeSpotted.length)); },
    flightOver: () => {},
    airborne: step => this.onAirborneStep(step),
  };

  /** The shot (if one is flying) to its end, then everyone airborne until they land: Match.runUntilSettled (64.1). */
  private settleAirborne(): void {
    const { timedOut } = this.match.runUntilSettled(this.sim);
    if (timedOut) {
      for (const p of this.state.players) {
        if (!p.alive || !p.airborne || !p.airbornePos || !p.airborneVel) continue;
        let nearest = Infinity;
        for (const pl of this.state.planets) {
          if (!pl.alive) continue;
          const d = dhypot(p.airbornePos.x - pl.position.x, p.airbornePos.y - pl.position.y, p.airbornePos.z - pl.position.z) - pl.radius;
          if (d < nearest) nearest = d;
        }
        const speed = dhypot(p.airborneVel.x, p.airborneVel.y, p.airborneVel.z);
        const r = dhypot(p.airbornePos.x, p.airbornePos.y, p.airbornePos.z);
        this.event('airborneTimeout', p.id, `${p.airborneCause ?? '?'} still airborne after ${MAX_AIRBORNE_TIME + 5}s: r ${r.toFixed(0)} nearest surface ${nearest.toFixed(1)} speed ${speed.toFixed(1)}`);
      }
    }
  }

  // ---- other actions -------------------------------------------------------------

  build(structureType: StructureType, surfaceDir: Vec3): void {
    const player = this.cur();
    // Match.build, as the game: ecotourism centre or listening-post antenna
    const b = this.turn('build', { structureType, surfaceDir }) as ReturnType<Match['build']>;
    if (!b.ok) return;
    this.act('build');
  }

  heal(itemKey: ItemKey): void {
    const player = this.cur();
    if (!this.turn('heal', { itemKey }).ok) return;
    this.act('heal');
  }

  /** Instant drone delivery; like the game's, it ends the turn. */
  shop(params: ShopParams): void {
    const player = this.cur();
    // Match.shop, as the game: ammo, fuel and heal items
    const bought = this.turn('shop', params) as ReturnType<Match['shop']>;
    if (!bought.ok) { this.endTurn(); return; }
    this.act('shop');
    this.event('shop', player.id, `${bought.total} cbp`);
    this.endTurn();
  }

  melee(targetId: number): void {
    const player = this.cur();
    const hit = this.turn('melee', { targetId }) as ReturnType<Match['melee']>;
    if (!hit.ok) return;
    this.act('melee');
    this.event('damage', player.id, String(hit.damage));
    if (hit.killed) this.event('kill', player.id, '1');
  }

  // ---- mode actions (mirrors of Game.perform*, minus meshes and HUD) --------------

  private refused(kind: string, why: string): void {
    this.unsupported++;
    this.event('refused', this.cur().id, `${kind}: ${why}`);
  }

  // Each mirrors Game.perform*: the Match acts, this host records the input and the event.
  private refusedBy(kind: string, r: { code?: string; reason: string }): void { this.refused(kind, r.code ?? r.reason); }

  plantFlag(surfaceDir: Vec3): void {
    const player = this.cur();
    const r = this.turn('plantFlag', { surfaceDir }) as ReturnType<Match['plantStake']>;
    if (!r.ok) { this.refusedBy('plantFlag', r); return; }
    this.act('plantStake');
    this.event('plantStake', player.id, `planet ${player.planetIndex}`);
  }

  removeStake(stakeId: number): void {
    const player = this.cur();
    const r = this.turn('removeStake', { stakeId }) as ReturnType<Match['removeStake']>;
    if (!r.ok) { this.refusedBy('removeStake', r); return; }
    this.act('removeStake');
    this.event('removeStake', player.id, String(stakeId));
  }

  pickup(objectId: number): void {
    const player = this.cur();
    const r = this.turn('pickup', { objectId }) as ReturnType<Match['pickup']>;
    if (!r.ok) { this.refusedBy('pickup', r); return; }
    this.act('pickup');
    this.event('pickup', player.id, r.object.type);
    if (r.captured !== null) {
      this.event('capture', player.id, String(r.captured));
      if (isGameOver(this.state)) this.gameOver = true;
    }
  }

  mount(bodyId: number): void {
    const player = this.cur();
    const r = this.turn('mount', { structureId: bodyId }) as ReturnType<Match['mount']>;
    if (!r.ok) { this.refusedBy('mount', r); return; }
    this.act('mount');
    this.event('mount', player.id, `emplacement ${bodyId}${r.captured ? ' captured' : ''}`);
  }

  interact(ruinId?: number): void {
    const player = this.cur();
    const r = this.turn('interact', ruinId === undefined ? {} : { ruinId }) as ReturnType<Match['interact']>;
    if (!r.ok) { this.refusedBy('interact', r); return; }
    if (r.kind === 'drill') {
      this.act('buildDrill');
      this.event('buildDrill', player.id, `planet ${player.planetIndex}`);
      return;
    }
    if (r.kind === 'activated') {
      for (const l of r.logs) this.event('log', player.id, l);
      this.event('interactDone', player.id, `ruin ${r.ruin.id}${r.signalRevealed ? ' signal revealed' : ''}`);
    }
    this.act('interact');
  }

  collect(params: CollectActionParams): void {
    const player = this.cur();
    const r = this.turn('collect', params) as ReturnType<Match['collect']>;
    if (!r.ok) { this.refusedBy('collect', r); return; }
    if (r.kind === 'ground') {
      this.act('collect');
      return;
    }
    this.act('collect');
    this.event('collect', player.id, `${r.collectible.type} value ${r.collectible.value}`);
  }

  deliver(carryableId?: number): void {
    const player = this.cur();
    const r = this.turn('deliver', carryableId === undefined ? {} : { carryableId }) as ReturnType<Match['deliver']>;
    if (!r.ok) { this.refusedBy('deliver', r); return; }
    this.act('deliver');
    if (r.kind === 'fragment') {
      this.event('deliver', player.id, 'fragment');
      if (isGameOver(this.state)) this.gameOver = true;
    } else {
      this.event('deliver', player.id, `salvage value ${r.object.value ?? 0}`);
    }
  }

  claim(ruinId: number): void {
    const player = this.cur();
    const r = this.turn('claim', { ruinId }) as ReturnType<Match['claim']>;
    if (!r.ok) { this.refusedBy('claim', r); return; }
    this.act('claim');
    this.event('claim', player.id, r.ruin.ruinType);
  }

  // ---- waits ---------------------------------------------------------------------

  async waitUntil(pred: () => boolean): Promise<void> {
    if (pred()) return;
    this.settleAirborne();
    if (!pred()) this.event('waitUnsatisfied', this.cur().id);
  }
  async delay(): Promise<void> { /* instant */ }
  async waitForFlightResolution(): Promise<void> { /* the flight resolved inside fire() */ }

  // ---- perception (Phase 57) --------------------------------------------------------

  /** Alert-ladder changes (Phase 57) as events. */
  private alertEvents(changes: ReturnType<Match['noise']>): void {
    for (const c of changes) this.event('alert', -1, `planet ${c.planetIndex} ${c.from} → ${c.to} (${c.cause})`);
  }

  /** Noise raises the alert ladder (no-op without the layer); every change is an event. */
  private noise(at: { point: Vec3; planetIndex: number; loudness: Loudness }): void {
    this.alertEvents(this.match.noise(at.point, at.planetIndex, at.loudness));
  }

  // ---- turn flow -----------------------------------------------------------------

  endTurn(): void {
    if (this.advancing || this.gameOver) return;
    this.advancing = true;
    this.endTurns++;
    try {
      // 64.6 step 2b-ii: the end of the turn is an input (`skip`, from the unit's brain); the Match passes it at its
      // boundary — advance the turn, open the next unit's window (a dead unit's turn passes on, hazards act on the unit
      // whose turn begins) — and this host walks the answer (Game's the same)
      const ended = this.turn('skip', {}) as PlanResult<{ pass: TurnPass }>;
      if (!ended.ok) { this.event('endRefused', this.cur().id, ended.reason); return; }
      this.followPass(ended.pass);
    } finally {
      this.advancing = false;
    }
  }

  /** The match's first window (64.6 step 2a): the current unit's turn begins, and passes on as at every boundary. */
  openFirstWindow(): void {
    this.advancing = true;
    try { this.followPass(this.match.openWindow(this.sim)); } finally { this.advancing = false; }
  }

  /** Walk a boundary's answer: game over, the pickup prompt, a round or a batch to resolve — then the window opens. */
  private followPass(pass: TurnPass): void {
    for (let step = 0; step <= this.state.players.length * 2; step++) {
      if (pass.next === 'gameOver') { this.gameOver = true; return; }
      if (pass.next === 'window' || pass.next === 'stalled') return;
      // Pickup (the end-of-match ferry) is a human prompt; bots take nothing
      if (pass.next === 'pickup') this.state = { ...this.state, phase: 'aiming' };
      // Round flows: the Match began a round (everyone-plans: the planning order wrapped; plan-resolve-together:
      // control left the planning teams) — run it out (bots never queue in the in-order batched flow)
      else if (pass.next === 'round') {
        this.resolveRound();
        if (this.gameOver) return;
        // 64.6 step 4: the policy opened the window as the round's airborne settled; only a round whose airborne never
        // came down (the cap: lost in orbit, forced down above) leaves it to this host
        if (this.windowPass) { pass = this.windowPass; this.windowPass = null; continue; }
      }
      pass = this.match.openWindow(this.sim);
    }
  }



  /**
   * A plan round (64.6 step 3): the Match began it at the turn boundary and its clock steps it live, as in the game;
   * this host runs it to its close, then whoever it left airborne until they land (runUntilSettled). The round is
   * reported as it closes (`roundClosed`): its skipped shots, each impact's damage, shelter and alert changes, the
   * landings within it, the probes' sightings — then the airborne it left land on later ticks.
   */
  private resolveRound(): void {
    this.roundsResolved++;
    this.settleAirborne();
    if (isGameOver(this.state)) this.gameOver = true;
  }

  private roundClosed(close: RoundClose): void {
    const { record } = close;
    for (const f of record.flights) {
      if (f.outcome === 'skipped') { if (f.submunitionOf === null) this.event('shotSkipped', f.ownerId, f.skipReason); continue; }
      this.flightSeconds += Math.max(0, f.points.length - 1) * PHYS_DT;
    }
    record.impacts.forEach((i, k) => {
      this.noteDamage(i.damages, i.kills.length, record.flights[i.flightIndex].ownerId);
      for (const id of i.sheltered ?? []) this.event('sheltered', id, 'rim');
      this.alertEvents(close.alerts[k]);
    });
    for (const a of record.airborne) {
      this.airborneSeconds += a.points.length * PHYS_DT;
      if (a.landedStep === null) continue;   // still airborne when the round ended: they land on the ticks after it
      this.recordLanding(a.playerId, a.cause, a.landingDamage, a.died);
      if (a.died) this.event(a.landingDamage > 0 ? 'crashDied' : 'lostInSpace', a.playerId, a.landingDamage > 0 ? `${a.cause} round dmg ${Math.round(a.landingDamage)}` : `${a.cause} space`);
      else this.event('landed', a.playerId, `${a.cause} round dmg ${Math.round(a.landingDamage)}`);
    }
    for (const sp of close.spotted) if (sp.unitIds.length > 0) this.event('probeSpotted', sp.ownerId, String(sp.unitIds.length));
    const flightsDesc = record.flights.filter(f => f.outcome !== 'skipped').map(f => `${f.submunitionOf !== null ? 'sub:' : ''}${f.weaponKey} ${f.outcome}@${f.endStep}`).join(', ');
    const walksDesc = record.walks.map(w => `p${w.playerId} ${w.arrived ? 'arrived' : 'cut'}@${w.fromStep + w.dirs.length}`).join(', ');
    const airDesc = record.airborne.map(a => `p${a.playerId} ${a.cause} ${a.died ? 'died' : a.landedStep === null ? 'still airborne' : 'landed'}@${a.landedStep ?? record.steps}`).join(', ');
    this.event('roundResolved', -1, `${record.steps} steps; flights [${flightsDesc}]; walks [${walksDesc}]; airborne [${airDesc}]`);
  }
}

// =============================================================================
// Driver
// =============================================================================

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

function skillName(skill: number): string {
  let best = 'medium';
  let bestD = Infinity;
  for (const [k, v] of Object.entries(BOT_SKILL_LEVELS)) {
    const d = Math.abs(v - skill);
    if (d < bestD) { bestD = d; best = k; }
  }
  return best;
}

/** Per-team objective standing at the end of a match, in the objective's own terms. */
export function objectiveStanding(s: GameState): Record<number, string> | null {
  const ms = s.modeState;
  const teams = [...new Set(s.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const out: Record<number, string> = {};
  if (ms.territory) {
    for (const t of teams) out[t] = `${ms.territory.territoryScores[t] ?? 0} planets`;
  } else if (ms.ctf) {
    for (const t of teams) out[t] = `${ms.ctf.scores[t] ?? 0} captures`;
  } else if (ms.signalTrace) {
    for (const t of teams) {
      const posts = (ms.signalTrace.clueState.activatedPosts[t] ?? []).length;
      const revealed = ms.signalTrace.clueState.signalRevealed[t] ? ', revealed' : '';
      const extracted = ms.signalTrace.extractionCompletedTeam === t ? ', extracted' : '';
      out[t] = `${posts} posts${revealed}${extracted}`;
    }
  } else if (ms.uplink) {
    for (const t of teams) out[t] = `${(ms.uplink.uploadedFragments[t] ?? []).length} fragments`;
  } else if (ms.salvage) {
    for (const t of teams) out[t] = `${ms.salvage.deliveredValue[t] ?? 0} salvage`;
  } else if (ms.excavation) {
    for (const t of teams) {
      const drills = ms.excavation.drillSites.filter(d => d.teamIndex === t && d.health > 0).length;
      const carrier = ms.excavation.artifactCarryableId != null
        ? s.carryables.find(c => c.id === ms.excavation!.artifactCarryableId)?.carriedBy : null;
      const holding = carrier != null && s.players.find(p => p.id === carrier)?.teamIndex === t ? ', holding artifact' : '';
      out[t] = `${drills} drills${holding}`;
    }
  } else {
    return null;
  }
  return out;
}

function killsByCause(events: HeadlessEvent[]): KillsByCause {
  const k: KillsByCause = { blast: 0, landing: 0, space: 0, jumpLanding: 0, jumpSpace: 0 };
  for (const e of events) {
    const byJump = e.detail?.startsWith('jump ') === true;
    if (e.kind === 'kill') k.blast += Number(e.detail ?? 1) || 1;
    else if (e.kind === 'crashDied') { k.landing++; if (byJump) k.jumpLanding++; }
    else if (e.kind === 'lostInSpace') { k.space++; if (byJump) k.jumpSpace++; }
  }
  return k;
}

/** Play a whole match headless and report its shape. */
export async function runHeadlessMatch(opts: HeadlessMatchOptions): Promise<MatchReport> {
  const t0 = performance.now();
  const { match, difficulty } = createMatch(opts);
  const maxRounds = opts.maxRounds ?? 40;
  // The kernel for line of sight and flights when it is loaded, as the game uses it
  match.wasmReady = isWasmReady();
  // a kernel world of its own, so battles resolved at once in one realm can't cross state at their awaits
  if (match.wasmReady) { match.kernelWorld = createKernelWorld(); match.enterKernel(); setPlanets(match.state.planets); }
  const host = new HeadlessHost(match);
  opts.onMatch?.(match);
  if (opts.traceTurns) host.turnTrace = [`t${match.state.turnNumber} p${match.state.currentPlayerIndex} ${(computeStateChecksum(match.state) >>> 0).toString(16).padStart(8, '0')}`];
  // The first unit's turn begins as every later one does (Game.startAimPhase)
  host.openFirstWindow();
  const runner = new BotTurnRunner(host);
  const computeMs: number[] = [];
  const turnsBy = new Map<number, number>();
  const movedBy = new Map<number, number>();
  const jumpedBy = new Map<number, number>();
  const checksums: number[] | undefined = opts.traceChecksums ? [] : undefined;

  let guard = 0;
  while (!host.gameOver && host.state.turnNumber <= maxRounds && guard < maxRounds * host.state.players.length * 4) {
    guard++;
    const cur = host.state.players[host.state.currentPlayerIndex];
    if (!cur.alive || !cur.isBot) { host.endTurn(); continue; }
    const endTurnsBefore = host.endTurns;
    host.beginTurn(cur.id);
    turnsBy.set(cur.id, (turnsBy.get(cur.id) ?? 0) + 1);
    const t1 = performance.now();
    await runner.runTurn(cur.id);
    computeMs.push(performance.now() - t1);
    checksums?.push(computeStateChecksum(host.state));
    const acts = host.turnActions[host.turnActions.length - 1].acts;
    if (acts.includes('move')) movedBy.set(cur.id, (movedBy.get(cur.id) ?? 0) + 1);
    if (acts.includes('jump')) jumpedBy.set(cur.id, (jumpedBy.get(cur.id) ?? 0) + 1);
    // The runner ends the turn itself unless its bot died or is still airborne mid-plan (a crash
    // landing after its own jump): the game's turn flow ends those turns, and so does this host.
    // Anything else is a stall worth knowing about.
    if (host.endTurns === endTurnsBefore && !host.gameOver) {
      const after = host.state.players.find(p => p.id === cur.id);
      const expected = !after || !after.alive || after.airborne;
      host.events.push({ round: host.state.turnNumber, playerId: cur.id, kind: expected ? 'turnEndedByHost' : 'turnNotEnded', detail: expected ? (after?.alive ? 'airborne' : 'died mid-turn') : undefined });
      host.endTurn();
    }
  }

  const s = host.state;
  opts.onFinished?.(s);
  host.match.dispose();
  const records = s.shotRecords ?? [];
  const primary = records.filter(r => !r.submunition);
  const win = checkWinCondition(s);
  const stats = summarizeShots(records);

  const firstDamage = host.events.find(e => e.kind === 'damage');
  const firstKill = host.events.find(e => e.kind === 'kill' || e.kind === 'crashDied' || e.kind === 'lostInSpace');
  const misses = primary.filter(r => r.missDistance !== null).map(r => r.missDistance!);
  const hits = primary.filter(r => r.damage > 0).length;
  const lost = primary.filter(r => r.outcome === 'lost').length;

  const bySkill: MatchReport['bySkill'] = {};
  for (const r of primary) {
    const p = s.players.find(x => x.id === r.shooterId);
    const k = skillName(p?.botConfig?.skill ?? 0.5);
    const b = bySkill[k] ?? (bySkill[k] = { shots: 0, hits: 0, medianMiss: null, assists: 0 });
    b.shots++;
    if (r.damage > 0) b.hits++;
    b.assists += r.assists;
  }
  for (const k of Object.keys(bySkill)) {
    bySkill[k].medianMiss = median(primary.filter(r => {
      const p = s.players.find(x => x.id === r.shooterId);
      return skillName(p?.botConfig?.skill ?? 0.5) === k && r.missDistance !== null;
    }).map(r => r.missDistance!));
  }

  const botTurns = host.turnActions.length;
  const movementTurns = host.turnActions.filter(t => t.acts.includes('move') || t.acts.includes('jump')).length;
  const fireOnly = host.turnActions.filter(t => t.acts.length > 0 && t.acts.every(a => a === 'fire' || a === 'probe')).length;
  const idle = host.turnActions.filter(t => t.acts.length === 0).length;

  return {
    seed: opts.seed,
    ...(s.botArchetypes ? { archetypes: s.botArchetypes } : {}),
    options: { teams: opts.teams ?? 2, unitsPerTeam: opts.unitsPerTeam ?? 1, planets: opts.planets ?? 6, maxRounds },
    unitsByTeam: Array.from({ length: opts.teams ?? 2 }, (_, t) => s.players.filter(p => p.teamIndex === t).length),
    cbpByTeam: Array.from({ length: opts.teams ?? 2 }, (_, t) => opts.cbpByTeam?.[t] ?? opts.cbp ?? 12),
    ...(s.modeState.alert ? { alert: {
      startingAlert: s.config.modeConfig.startingAlert ?? 'unaware',
      guardTeams: s.config.modeConfig.guardTeams ?? 'all',
      firstSuspiciousRound: host.events.find(e => e.kind === 'alert' && / → suspicious/.test(e.detail ?? ''))?.round ?? null,
      firstAlertRound: host.events.find(e => e.kind === 'alert' && / → alert/.test(e.detail ?? ''))?.round ?? null,
      wentLoud: s.modeState.alert.wentLoud,
      finalLevels: Object.fromEntries(Object.entries(s.modeState.alert.byPlanet).map(([k, v]) => [k, v.level])),
      infiltratorLandings: summarizeInfiltratorLandings(host.landings, s.config.modeConfig.guardTeams ?? 'all'),
    } } : {}),
    difficulty,
    mode: matchDisplayName(s.config.modeConfig),
    turnFlow: s.config.turnFlow ?? 'perUnit',
    roundsResolved: host.roundsResolved,
    kits: Array.from({ length: opts.teams ?? 2 }, (_, t) => { if (opts.kitByUnit?.[t]?.some(k => k !== undefined)) return 'per-unit'; const k = opts.kitByTeam?.[t] ?? opts.kit; return k === undefined ? 'standard' : typeof k === 'string' ? k : 'custom'; }),
    rounds: s.turnNumber,
    botTurns,
    winnerTeam: win?.winningTeamIndex ?? null,
    reason: win?.reason ?? (host.gameOver ? 'gameover' : null),
    killsByCause: killsByCause(host.events),
    objective: objectiveStanding(s),
    firstDamageRound: firstDamage?.round ?? null,
    firstKillRound: firstKill?.round ?? null,
    shots: primary.length,
    hits,
    hitRate: primary.length ? hits / primary.length : 0,
    lost,
    lostRate: primary.length ? lost / primary.length : 0,
    assists: records.reduce((a, r) => a + r.assists, 0),
    highlightShots: records.filter(r => isHighlightShot(r, PHYS_DT)).length,
    meanMiss: misses.length ? misses.reduce((a, b) => a + b, 0) / misses.length : null,
    medianMiss: median(misses),
    bySkill,
    actions: { ...host.actions },
    movementTurnRate: botTurns ? movementTurns / botTurns : 0,
    fireOnlyTurnRate: botTurns ? fireOnly / botTurns : 0,
    idleTurnRate: botTurns ? idle / botTurns : 0,
    // X11: the call-ins asked and answered (only with the flag, so the golden's reports keep their shape)
    ...(opts.callIns ? { callIns: { asked: (s.callIns ?? []).length, fired: (s.callIns ?? []).filter(c => c.answer === 'fired').length, refused: (s.callIns ?? []).filter(c => c.answer === 'refused').length, missions: host.asks.missions, marks: host.asks.marks } } : {}),
    flightSeconds: host.flightSeconds,
    airborneSeconds: host.airborneSeconds,
    meanComputeMs: computeMs.length ? computeMs.reduce((a, b) => a + b, 0) / computeMs.length : 0,
    maxComputeMs: computeMs.length ? Math.max(...computeMs) : 0,
    totalMs: performance.now() - t0,
    unsupported: host.unsupported,
    ...(checksums ? { checksums: [...checksums, computeStateChecksum(s)] } : {}),
    ...(host.turnTrace ? { turnTrace: host.turnTrace } : {}),
    players: s.players.map(p => ({
      id: p.id, name: p.name, team: p.teamIndex, skill: p.botConfig?.skill ?? 0,
      alive: p.alive, health: p.health, damageDealt: p.damageDealt, kills: p.kills,
      shots: stats.get(p.id)?.shots ?? 0, hits: stats.get(p.id)?.hits ?? 0, assists: stats.get(p.id)?.assists ?? 0,
      turns: turnsBy.get(p.id) ?? 0, movedTurns: movedBy.get(p.id) ?? 0, jumpedTurns: jumpedBy.get(p.id) ?? 0,
    })),
    events: host.events,
    trace: host.trace,
  };
}

// =============================================================================
// Aggregation over many seeds
// =============================================================================

export interface BatchSummary {
  matches: number;
  decided: number;
  meanRounds: number;
  medianRounds: number | null;
  firstDamageMedian: number | null;
  firstKillMedian: number | null;
  shots: number;
  hitRate: number;
  lostRate: number;
  medianMiss: number | null;
  assistsPerMatch: number;
  highlightsPerMatch: number;
  movementTurnRate: number;
  fireOnlyTurnRate: number;
  idleTurnRate: number;
  flightSecondsPerMatch: number;
  airborneSecondsPerMatch: number;
  meanComputeMs: number;
  winsByTeam: Record<number, number>;
  /** How matches ended: elimination / objective / turnLimit / undecided. */
  reasons: Record<string, number>;
  killsByCause: KillsByCause;
  bySkill: Record<string, { shots: number; hitRate: number; medianMiss: number | null; assistsPerShot: number }>;
  actionsPerMatch: Record<string, number>;
  unsupported: number;
}

export function summarizeReports(reports: MatchReport[]): BatchSummary {
  const n = reports.length || 1;
  const sum = (f: (r: MatchReport) => number) => reports.reduce((a, r) => a + f(r), 0);
  const shots = sum(r => r.shots);
  const wins: Record<number, number> = {};
  for (const r of reports) if (r.winnerTeam !== null) wins[r.winnerTeam] = (wins[r.winnerTeam] ?? 0) + 1;
  const reasons: Record<string, number> = {};
  for (const r of reports) {
    const k = r.winnerTeam === null ? 'undecided' : (r.reason ?? 'unknown');
    reasons[k] = (reasons[k] ?? 0) + 1;
  }
  const kills: KillsByCause = { blast: 0, landing: 0, space: 0, jumpLanding: 0, jumpSpace: 0 };
  for (const r of reports) {
    for (const key of Object.keys(kills) as Array<keyof KillsByCause>) kills[key] += r.killsByCause[key] ?? 0;
  }

  const skillAgg: Record<string, { shots: number; hits: number; assists: number; misses: number[] }> = {};
  for (const r of reports) {
    for (const [k, v] of Object.entries(r.bySkill)) {
      const a = skillAgg[k] ?? (skillAgg[k] = { shots: 0, hits: 0, assists: 0, misses: [] });
      a.shots += v.shots; a.hits += v.hits; a.assists += v.assists;
      if (v.medianMiss !== null) a.misses.push(v.medianMiss);
    }
  }
  const bySkill: BatchSummary['bySkill'] = {};
  for (const [k, a] of Object.entries(skillAgg)) {
    bySkill[k] = { shots: a.shots, hitRate: a.shots ? a.hits / a.shots : 0, medianMiss: median(a.misses), assistsPerShot: a.shots ? a.assists / a.shots : 0 };
  }

  const actions: Record<string, number> = {};
  for (const r of reports) for (const [k, v] of Object.entries(r.actions)) actions[k] = (actions[k] ?? 0) + v / n;

  const botTurns = sum(r => r.botTurns) || 1;
  return {
    matches: reports.length,
    decided: reports.filter(r => r.winnerTeam !== null).length,
    meanRounds: sum(r => r.rounds) / n,
    medianRounds: median(reports.map(r => r.rounds)),
    firstDamageMedian: median(reports.map(r => r.firstDamageRound).filter((x): x is number => x !== null)),
    firstKillMedian: median(reports.map(r => r.firstKillRound).filter((x): x is number => x !== null)),
    shots,
    hitRate: shots ? sum(r => r.hits) / shots : 0,
    lostRate: shots ? sum(r => r.lost) / shots : 0,
    medianMiss: median(reports.map(r => r.medianMiss).filter((x): x is number => x !== null)),
    assistsPerMatch: sum(r => r.assists) / n,
    highlightsPerMatch: sum(r => r.highlightShots) / n,
    movementTurnRate: sum(r => r.movementTurnRate * r.botTurns) / botTurns,
    fireOnlyTurnRate: sum(r => r.fireOnlyTurnRate * r.botTurns) / botTurns,
    idleTurnRate: sum(r => r.idleTurnRate * r.botTurns) / botTurns,
    flightSecondsPerMatch: sum(r => r.flightSeconds) / n,
    airborneSecondsPerMatch: sum(r => r.airborneSeconds) / n,
    meanComputeMs: sum(r => r.meanComputeMs * r.botTurns) / botTurns,
    winsByTeam: wins,
    reasons,
    killsByCause: kills,
    bySkill,
    actionsPerMatch: actions,
    unsupported: sum(r => r.unsupported),
  };
}

